/**
 * Hub mode (Discord-like): the server rail, the channel sidebar, and the
 * main pane (a text channel, a voice room, or Home: DMs and Friends).
 */
import {
  ChannelType,
  NxPermissions,
  Permissions,
  ROLE_ICONS_SHOWN_MAX,
  type Channel,
  type Snowflake,
  type VoiceState,
} from '@neosocial/protocol';
import { memo, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { tr, useI18n, useT } from '../i18n';
import { storage } from '../lib/storage';
import { useFlag, useRoleIconMode } from '../store/config';
import { useData } from '../store/data';
import { useDataShallow } from '../store/data';
import {
  basePermissions,
  can,
  channelPermissions,
  dmUnread,
  iconRoles,
  officialGuildId,
  pendingIncoming,
  presenceOf,
  railGuildIds,
  unreadOf,
  useActiveGuildId,
  userName,
  viewAsRoles,
  voiceIconMax,
  voiceStatesIn,
} from '../store/selectors';
import { ViewAsBar } from '../admin/ViewAs';
import { canSeeVoiceMutes } from '../admin/VoiceMutes';
import { canSeeEmojiTab } from '../admin/EmojiTab';
import { RoleIconGroup, RoleIconView, roleIconGroupWidth } from './RoleIcon';
import { useChannelTree } from '../store/selectors';
import { goHub, openAdmin, useUi, type HubView } from '../store/ui';
import { userSpeaking } from '../voice/machine';
import { useVoice } from '../voice/store';
import { Avatar, Icon, type IconName } from '../ui/kit';
import { openMenu, overlayOpen, rememberAnchor, type Anchor, type MenuItem } from '../ui/overlay';
import markUrl from '../assets/mark.svg';
import { ChannelView } from './ChannelView';
import { DmSidebar, FriendsPage } from './Home';
import { openProfile } from './ProfileCard';
import { openMemberMenu } from '../mod/memberMenu';
import { MediaBadges, RowFlags, useMediaOf, useVoiceFlags } from '../voice/VoiceFlags';
import { PriorityBadge, VoiceStatusLine } from '../voice/VoiceExtras';
import { serverTools } from '../mod/perms';
import { joinVoiceFromUi } from './voiceUi';
import { joinAndWatch } from './streamUi';
import { rowMenuExtra, VoiceRoom } from './VoiceRoom';
import { RequestsPage } from './Requests';
import { ackChannel } from '../store/messages';
import { useUm } from '../store/um';
import { memberTargetState, useSidebarDrag } from './sidebarDnd';
import { useSidebarDnd } from './useSidebarDnd';
import { openJump } from '../shell/Jump';
import { useLayout } from '../shell/panes';
// threads (2026-10-29): joined threads under their channel; a thread beside it, or full screen
import { threadOfView } from '../threads/actions';
import { SidebarThreads } from '../threads/entry';
import { ThreadPage } from '../threads/ThreadPage';
import { channelNotifyItems, guildNotifyItems } from '../notify/menus';
import { ChannelEventStrip, EventsHeaderRow, useEventMenuItems } from '../events/Sidebar';
// many servers (Console v2): the rail's servers, + Add a Server, Leave and Delete
import { AddServer } from '../servers/AddServer';
import { GuildIcon } from '../servers/GuildIcon';
import { DeleteServerHost, serverExitItems } from '../servers/menu';
import { RailGuild } from '../servers/RailGuild';

export function Banner({ colors = ['#7c3aed', '#22d3ee'] }: { colors?: [string, string] }) {
  return (
    <svg
      className="nx-art"
      viewBox="0 0 640 360"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ns-banner-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={colors[0]} />
          <stop offset="1" stopColor={colors[1]} />
        </linearGradient>
        <radialGradient id="ns-banner-b" cx=".2" cy=".1" r=".8">
          <stop offset="0" stopColor="#fff" stopOpacity=".35" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="640" height="360" fill="url(#ns-banner-a)" />
      <rect width="640" height="360" fill="url(#ns-banner-b)" />
      <g fill="none" stroke="#fff" strokeOpacity=".16" strokeWidth="1.2">
        <circle cx="520" cy="80" r="120" />
        <circle cx="520" cy="80" r="170" />
        <circle cx="520" cy="80" r="220" />
        <circle cx="90" cy="330" r="90" />
      </g>
    </svg>
  );
}

/**
 * A channel's type icon, in one place for every list (P32). A stage channel
 * is a voice channel and draws the speaker, as every voice channel does
 * (channel mute QA, 2026-10-28: never '#'); text channels draw '#'.
 */
export function channelIcon(c: Pick<Channel, 'type'>): IconName {
  return c.type === ChannelType.GUILD_STAGE_VOICE || c.type === ChannelType.GUILD_VOICE
    ? 'volume'
    : 'hash';
}

/**
 * Where a row's context menu opens: at the pointer, or on the row itself
 * when the keyboard asked (Shift+F10 or the menu key give no pointer).
 */
function menuPoint(e: React.MouseEvent<HTMLElement>): Anchor {
  return e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : e.currentTarget;
}

/** A row's label cut off by its width says itself in a tooltip (I24). */
function tipWhenCut(e: React.PointerEvent<HTMLElement>) {
  const n = e.currentTarget.querySelector<HTMLElement>('.ch-n');
  if (!n) return;
  if (n.scrollWidth > n.clientWidth + 1) n.setAttribute('data-tip', n.textContent ?? '');
  else n.removeAttribute('data-tip');
}

/* ---------------- the rail ---------------- */

function Rail() {
  const { t, fmt } = useT();
  const view = useUi((u) => u.hub);
  const home = view?.kind === 'home';
  const ignored = useUm((s) => s.ignored);
  const dms = useData((d) => dmUnread(d, ignored));
  // incoming friend requests count too, as on Discord's Home button (QA
  // 2026-10-28: only "Friends 1" inside Home said so)
  const requests = useData(pendingIncoming);
  const homeBadge = dms + requests;
  // the official server first, then your order, then by join (Console v2)
  const servers = useDataShallow(railGuildIds);
  // the badges are drawn beside the buttons: their counts go in the buttons' names
  const homeLabel =
    t('hub.home') +
    (dms ? ', ' + t('ns.rail.unread', { n: dms }) : '') +
    (requests ? ', ' + t('ns.rail.requests', { n: requests }) : '');
  // phones and tablets have no title bar on the web: Jump to… heads the rail (§12)
  const compact = useLayout().layout !== 'desktop';
  return (
    <nav className="hub-rail" aria-label={t('hub.a11y.servers')}>
      {compact ? (
        <div className="rail-item rail-jump">
          <button
            type="button"
            className="rail-btn jump"
            aria-label={t('ns.tb.jump')}
            aria-keyshortcuts="Control+K"
            onClick={(e) => openJump(e.currentTarget)}
          >
            <Icon name="search" size="l" />
          </button>
        </div>
      ) : null}
      <div className={'rail-item' + (home ? ' sel' : '')}>
        <span className="rail-pill" aria-hidden="true" />
        <button
          type="button"
          className="rail-btn home"
          aria-label={homeLabel}
          data-tip={t('hub.home')}
          data-tip-pos="end"
          aria-current={home ? 'page' : undefined}
          onClick={() => goHub({ kind: 'home' })}
        >
          <Icon name="chats" size="l" />
        </button>
        {homeBadge ? (
          <b className="nx-badge rail-badge" aria-hidden="true">
            {fmt.num(homeBadge)}
          </b>
        ) : null}
      </div>
      <hr className="rail-sep" aria-hidden="true" />
      {servers.map((id) => (
        <RailGuild key={id} guildId={id} />
      ))}
      <hr className="rail-sep" aria-hidden="true" />
      {/* Discord's "+ Add a Server": create your own or join one (servers/AddServer.tsx) */}
      <AddServer />
    </nav>
  );
}

/* ---------------- the channel sidebar ---------------- */

/**
 * A click on someone live in the sidebar (Discord's): Watch stream (from
 * elsewhere it joins first; either way the room opens) or View profile, at
 * their row.
 */
function openStreamerMenu(
  row: HTMLElement,
  channelId: Snowflake,
  userId: Snowflake,
  name: string,
): void {
  const { t } = tr();
  const s = useVoice.getState().session;
  const here = s.channelId === channelId && s.status === 'connected';
  openMenu(
    row,
    [
      {
        id: 'watch-stream',
        label: t(here ? 'stream.watch' : 'stream.watchJoin'),
        icon: 'eye',
        run: () =>
          void joinAndWatch(channelId, userId).then(
            (ok) => ok && goHub({ kind: 'voice', channelId }),
          ),
      },
      {
        id: 'profile',
        label: t('pres.profile'),
        icon: 'user',
        run: () => openProfile(userId, row),
      },
    ],
    { label: name },
  );
}

/**
 * One person under a voice channel: [role icon] [avatar] name [flags]. The
 * role icon slot is kept (empty) for people without one when anyone in the
 * channel has an icon, so the avatars stay in one column.
 *
 * With the console's `display.role_icons` = `all`, the slot is a group of
 * icons (the highest nearest the avatar, then "+N"), as wide as the
 * channel's widest group (`iconCols` icons, and a chip as wide as the
 * channel's biggest "+N", `iconMore`): people with fewer icons get empty
 * space, and the avatars still line up.
 */
function VoiceMemberRow({
  vs,
  guildId,
  iconCols,
  iconMore,
}: {
  vs: VoiceState;
  guildId: Snowflake;
  /** The channel's most icons in one row (capped), 0 when nobody has one. */
  iconCols: number;
  /** The channel's biggest "+N" (`all` mode), 0 when nobody has a chip. */
  iconMore: number;
}) {
  const mode = useRoleIconMode();
  const user = useData((d) => d.users[vs.user_id]);
  const name = useData((d) => userName(d, vs.user_id, guildId));
  const roles = useDataShallow((d) =>
    iconRoles(d.guilds[guildId], d.members[guildId]?.[vs.user_id], mode),
  );
  const role = roles[0];
  const meId = useData((d) => d.me?.id);
  const speaking = useVoice(
    (v) => v.session.status === 'connected' && userSpeaking(v.session, vs.user_id, meId),
  );
  // every state at once, a moderator's apart (live bug 2026-10-28)
  const flags = useVoiceFlags(vs.user_id, guildId);
  // LIVE and the camera mark after the flags (screen share and video, §4.7)
  const media = useMediaOf(vs.user_id, guildId);
  // lifted while it is dragged onto another channel
  const lifted = useSidebarDrag((s) => s.drag?.kind === 'member' && s.drag.userId === vs.user_id);
  return (
    <li>
      <button
        type="button"
        className={'vc-u' + (speaking ? ' is-speaking' : '')}
        data-user={vs.user_id}
        data-dragging={lifted || undefined}
        tabIndex={-1}
        onClick={(e) => {
          const el = e.currentTarget;
          // someone else live: Watch stream or View profile, a small menu at the row (Discord's)
          if (media.live && vs.user_id !== meId && vs.channel_id) {
            rememberAnchor(el);
            openStreamerMenu(el, vs.channel_id, vs.user_id, name);
          } else openProfile(vs.user_id, el);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          const el = e.currentTarget;
          // View profile opens here even if the person has left voice by then
          rememberAnchor(el);
          // with Volume and "Mute for me" while they are in your channel, as a tile's
          openMemberMenu(menuPoint(e), guildId, vs.user_id, rowMenuExtra(vs.user_id, meId, el));
        }}
      >
        {mode === 'all' ? (
          iconCols ? (
            <RoleIconGroup
              roles={roles}
              cls="vc-ri"
              width={roleIconGroupWidth(iconCols, iconMore)}
            />
          ) : null
        ) : role ? (
          <RoleIconView role={role} size={16} cls="vc-ri" />
        ) : iconCols ? (
          <span className="ns-ri vc-ri none" aria-hidden="true" />
        ) : null}
        <Avatar user={user} name={name} size={20} speaking={speaking} />
        <span className="vc-n" dir="auto">
          {name}
        </span>
        <PriorityBadge userId={vs.user_id} cls="vc-prio" />
        {flags.length ? (
          // their own box: the name keeps its room, the flags never spill
          // past the row (QA 2026-10-28, channel mute round 2), and as many
          // as the row has room for beside the role icons, the rest in "+N"
          <RowFlags flags={flags} cls="vc-fs" reset={`${mode}:${iconCols}:${iconMore}`} />
        ) : null}
        {media.live || media.camera ? (
          <span className="vc-media">
            <MediaBadges live={media.live} camera={media.camera} cls="vc-f" />
          </span>
        ) : null}
      </button>
    </li>
  );
}

const ChannelRow = memo(function ChannelRow({
  c,
  sel,
  canManage,
  noConnect = false,
}: {
  c: Channel;
  sel: boolean;
  canManage: boolean;
  /** A voice channel your roles can't connect to (Discord shows a lock). */
  noConnect?: boolean;
}) {
  const { t, fmt } = useT();
  const st = useDataShallow((d) => unreadOf(d, c.id));
  const voice = c.type === ChannelType.GUILD_VOICE || c.type === ChannelType.GUILD_STAGE_VOICE;
  const gid = c.guild_id ?? '';
  const lang = useI18n((s) => s.lang);
  // Ordered by highest role, then name (store/voiceOrder.ts); re-sorts live on role changes.
  const members = useDataShallow((d) => (voice ? voiceStatesIn(d, gid, c.id, lang) : EMPTY));
  // the role icon slot every row keeps, so the avatars stay in one column:
  // one number per store update, worked out without sorting (voiceIconMax)
  const iconMode = useRoleIconMode();
  const iconMax = useData((d) => (voice ? voiceIconMax(d, gid, members, iconMode) : 0));
  const iconCols = Math.min(ROLE_ICONS_SHOWN_MAX, iconMax);
  const iconMore = Math.max(0, iconMax - ROLE_ICONS_SHOWN_MAX);
  const myChannel = useVoice((v) => v.session.channelId);
  // muted (notification settings): dimmed, no unread dot; its mentions still show
  const status = st.mentions ? 'mention' : st.unread ? 'unread' : st.muted ? 'muted' : undefined;
  const label =
    (voice ? '' : '#') +
    (c.name ?? '') +
    (st.mentions
      ? ', ' + t('hub.ch.mentions', { n: st.mentions })
      : st.unread
        ? ', ' + t('hub.ch.unread')
        : '') +
    (st.muted ? ', ' + t('nt.muted') : '');
  const full = !!c.user_limit && members.length >= c.user_limit;
  // the row keeps its hover fill while its menu is open (I25)
  const [menuOpen, setMenuOpen] = useState(false);
  const open = () => {
    if (voice) {
      if (myChannel === c.id) goHub({ kind: 'voice', channelId: c.id });
      else void joinVoiceFromUi(c.id);
    } else goHub({ kind: 'channel', channelId: c.id });
  };
  const locked = voice && (c.nx_has_password || full || noConnect);
  // while someone in voice is dragged: lit when they may be moved here;
  // while this channel is dragged: lifted
  const dnd = useSidebarDrag((s) => (voice ? memberTargetState(s.drag, c.id) : undefined));
  const lifted = useSidebarDrag((s) => s.drag?.kind === 'channel' && s.drag.id === c.id);
  return (
    <li>
      <button
        type="button"
        className="ch"
        data-type={voice ? 'voice' : 'text'}
        data-st={status}
        data-ch={c.id}
        data-dnd={dnd}
        data-dragging={lifted || undefined}
        aria-current={sel ? 'page' : undefined}
        aria-expanded={menuOpen || undefined}
        aria-label={label}
        tabIndex={sel ? 0 : -1}
        onClick={open}
        onPointerEnter={tipWhenCut}
        onContextMenu={(e) => {
          e.preventDefault();
          const items: MenuItem[] = [];
          // only something unread can be marked read: a text channel, or a voice channel's own chat
          if (st.unread || st.mentions)
            items.push({ label: t('ns.ch.markRead'), icon: 'check', run: () => ackChannel(c.id) });
          if (voice)
            items.push(
              {
                label: t('ns.ch.openChat'),
                icon: 'chats',
                run: () => goHub({ kind: 'voice', channelId: c.id, chat: true }),
              },
              { label: t('c.join'), icon: 'volume', run: open },
            );
          // Mute channel ›, Notification settings › (notify/menus.ts)
          const notify = channelNotifyItems(c);
          if (notify.length) {
            if (items.length) items.push({ sep: true });
            items.push(...notify);
          }
          if (canManage) {
            if (items.length) items.push({ sep: true });
            items.push({
              label: t('hub.ch.settings'),
              icon: 'gear',
              run: () => openAdmin({ tab: 'channels', id: c.id }),
            });
          }
          // a read text channel offers a member nothing: no empty menu
          if (!items.length) return;
          setMenuOpen(true);
          openMenu(menuPoint(e), items, { onClose: () => setMenuOpen(false) });
        }}
      >
        <span className="ch-i">
          <Icon name={channelIcon(c)} />
        </span>
        <span className="ch-n" dir="auto">
          {c.name}
        </span>
        {canManage ? (
          <span className="ch-tools">
            <span
              className="ch-tool"
              role="button"
              tabIndex={-1}
              aria-label={t('hub.ch.settings')}
              data-tip={t('hub.ch.settings')}
              onClick={(e) => {
                e.stopPropagation();
                openAdmin({ tab: 'channels', id: c.id });
              }}
            >
              <Icon name="gear" />
            </span>
          </span>
        ) : null}
        {locked || (voice && c.user_limit) || st.mentions ? (
          <span className="ch-end">
            {locked ? (
              <i
                className="ch-lock"
                {...(noConnect ? { 'data-tip': t('hub.vc.noConnect'), 'data-no-connect': '' } : {})}
              >
                <Icon name="lock" />
              </i>
            ) : null}
            {voice && c.user_limit ? (
              <span
                className={'vc-cap nx-num' + (full ? ' full' : '')}
                data-tip={
                  full ? t('hub.vc.fullTip', { n: members.length, max: c.user_limit }) : undefined
                }
              >
                {fmt.num(members.length)}/{fmt.num(c.user_limit)}
              </span>
            ) : null}
            {st.mentions ? <b className="nx-badge">{fmt.num(st.mentions)}</b> : null}
          </span>
        ) : null}
      </button>
      {!voice ? <SidebarThreads parent={c} /> : null}
      {voice ? <ChannelEventStrip channelId={c.id} /> : null}
      {voice ? <VoiceStatusLine channelId={c.id} /> : null}
      {voice && members.length ? (
        <ul className="vc-mem">
          {members.map((vs) => (
            <VoiceMemberRow
              key={vs.user_id}
              vs={vs}
              guildId={gid}
              iconCols={iconCols}
              iconMore={iconMore}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
});

const EMPTY: VoiceState[] = [];
const NO_IDS: Snowflake[] = [];

/** A group of menu items after a separator, or nothing when it is empty. */
const withSep = (items: MenuItem[]): MenuItem[] => (items.length ? [{ sep: true }, ...items] : []);

/** A server you made has its own colours on the banner card, from its id. */
function bannerColorsOf(guildId: Snowflake): [string, string] {
  const PAIRS: [string, string][] = [
    ['#7c3aed', '#22d3ee'],
    ['#2563eb', '#a78bfa'],
    ['#db2777', '#f59e0b'],
    ['#059669', '#38bdf8'],
    ['#ea580c', '#e11d48'],
    ['#4f46e5', '#14b8a6'],
  ];
  let h = 0;
  for (const ch of guildId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PAIRS[h % PAIRS.length]!;
}

function ServerHeader() {
  const { t } = useT();
  // the open server (Console v2: many servers)
  const gid = useActiveGuildId() ?? '';
  const guild = useData((d) => d.guilds[gid]);
  const official = useData((d) => officialGuildId(d) === gid);
  // members of this server only (presences also hold friends from elsewhere), as the member list counts
  const online = useData((d) => {
    let n = 0;
    for (const id of Object.keys(d.members[gid] ?? {})) if (presenceOf(d, id) !== 'offline') n++;
    return n;
  });
  const members = useData((d) => Object.keys(d.members[gid] ?? {}).length);
  const perms = useDataShallow((d) => basePermissions(d, gid || null));
  const canChannels = can(perms, Permissions.MANAGE_CHANNELS);
  const canRoles = can(perms, Permissions.MANAGE_ROLES);
  const tools = useDataShallow((d) => serverTools(d, gid || null));
  const automodOn = useFlag('automod');
  const extraTools = [
    tools.overview && {
      label: t('ns.admin.overview'),
      icon: 'gear' as const,
      run: () => openAdmin({ tab: 'overview' }),
    },
    // custom emoji (2026-11-01): Create or Manage Expressions
    canSeeEmojiTab(perms) && {
      label: t('emo.tab'),
      icon: 'smile' as const,
      run: () => openAdmin({ tab: 'emoji' }),
    },
    tools.invites && {
      label: t('um.admin.invites'),
      icon: 'link' as const,
      run: () => openAdmin({ tab: 'invites' }),
    },
    // AutoMod (Manage Server, while the console has it on)
    tools.overview &&
      automodOn && {
        label: t('am.tab'),
        icon: 'shield-check' as const,
        run: () => openAdmin({ tab: 'automod' }),
      },
    tools.audit && {
      label: t('um.admin.audit'),
      icon: 'history' as const,
      run: () => openAdmin({ tab: 'audit' }),
    },
    tools.bans && {
      label: t('um.admin.bans'),
      icon: 'cm-ban' as const,
      run: () => openAdmin({ tab: 'bans' }),
    },
    tools.deleted && {
      label: t('um.admin.deleted'),
      icon: 'trash' as const,
      run: () => openAdmin({ tab: 'deleted' }),
    },
    tools.owner && {
      label: t('um.admin.owner'),
      icon: 'crown' as const,
      run: () => openAdmin({ tab: 'owner' }),
    },
  ].filter((x): x is { label: string; icon: 'link'; run: () => void } => !!x);
  // scheduled events: "Events" and "Create event"
  const eventItems = useEventMenuItems();
  // the chevron turns while the menu is open (§6.4)
  const [menuOpen, setMenuOpen] = useState(false);
  if (!guild) return null;
  return (
    <header className="srv-head" data-banner="">
      {/* the banner card of 58ceeb8 (owner, 2026-10-29) */}
      <div className="srv-banner" aria-hidden="true">
        {official ? (
          <>
            <Banner />
            <img className="srv-mark" src={markUrl} alt="" />
          </>
        ) : (
          // a server someone made: its own colours and its icon or initials
          <>
            <Banner colors={bannerColorsOf(guild.id)} />
            <span className="srv-mark sv-mark">
              <GuildIcon guildId={guild.id} />
            </span>
          </>
        )}
      </div>
      <button
        type="button"
        className="srv-name"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t('hub.srv.menu')}
        onClick={(e) => {
          setMenuOpen(true);
          openMenu(
            e.currentTarget,
            [
              { header: guild.name },
              // the counts the old strip under the banner showed (L2)
              {
                header: t('ns.srv.counts', {
                  online: t('c.online', { n: online }),
                  members: t('c.members', { n: members }),
                }),
              },
              { sep: true },
              ...(eventItems.length ? [...eventItems, { sep: true }] : []),
              ...(canChannels
                ? [
                    {
                      label: t('ns.admin.newChannel'),
                      icon: 'plus-circle' as const,
                      run: () => openAdmin({ tab: 'channels', id: 'new', newType: 0 }),
                    },
                    {
                      label: t('ns.admin.newCategory'),
                      icon: 'list' as const,
                      run: () => openAdmin({ tab: 'channels', id: 'new', newType: 4 }),
                    },
                    {
                      label: t('ns.admin.manageChannels'),
                      icon: 'hash' as const,
                      run: () => openAdmin({ tab: 'channels' }),
                    },
                  ]
                : []),
              ...(canRoles
                ? [
                    {
                      label: t('ns.admin.manageRoles'),
                      icon: 'shield' as const,
                      run: () => openAdmin({ tab: 'roles' }),
                    },
                  ]
                : []),
              ...(extraTools.length && (canChannels || canRoles) ? [{ sep: true }] : []),
              ...extraTools,
              ...(!canChannels && !canRoles && !extraTools.length
                ? [{ label: t('ns.admin.noTools'), icon: 'info' as const, disabled: true }]
                : []),
              // Mute server ›, Notification settings (notify/menus.ts)
              ...withSep(guildNotifyItems(guild.id)),
              // Leave Server, or the owner's Delete Server (servers/menu.tsx)
              ...withSep(serverExitItems(guild.id)),
            ],
            { placement: 'bottom-start', onClose: () => setMenuOpen(false) },
          );
        }}
      >
        {official ? (
          <span className="srv-verified" data-tip={t('hub.official')}>
            <Icon name="check" />
          </span>
        ) : null}
        <b dir="auto">{guild.name}</b>
        <Icon name="chev-down" cls="chev" />
      </button>
      <div className="srv-counts">
        <span>
          <i className="dot on" />
          {t('c.online', { n: online })}
        </span>
        <span>
          <i className="dot" />
          {t('c.members', { n: members })}
        </span>
      </div>
      <EventsHeaderRow />
    </header>
  );
}

function ChannelSidebar() {
  const { t } = useT();
  const gid = useActiveGuildId();
  const tree = useChannelTree(gid);
  const view = useUi((u) => u.hub);
  const myVoice = useVoice((v) => v.session.channelId);
  // "+" on a category: only where you may make channels (Manage channels in it, as the server checks).
  const manageableCats = useDataShallow((d) =>
    Object.values(d.channels)
      .filter(
        (c) =>
          c.guild_id === gid &&
          c.type === ChannelType.GUILD_CATEGORY &&
          can(channelPermissions(d, c), Permissions.MANAGE_CHANNELS),
      )
      .map((c) => c.id),
  );
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    storage.getJson('hub-collapsed', {}),
  );
  const toggle = (id: string) => {
    const next = { ...collapsed, [id]: !collapsed[id] };
    setCollapsed(next);
    storage.setJson('hub-collapsed', next);
  };
  const selId = view && view.kind !== 'home' ? view.channelId : null;
  // "View server as role": hide what those roles can't see (the server
  // already leaves out what you yourself can't).
  const previewHidden = useDataShallow((d) =>
    viewAsRoles(d, gid)
      ? Object.values(d.channels)
          .filter(
            (c) => c.guild_id === gid && !can(channelPermissions(d, c), Permissions.VIEW_CHANNEL),
          )
          .map((c) => c.id)
      : NO_IDS,
  );
  const shown = previewHidden.length
    ? tree
        .map((n) => ({ ...n, channels: n.channels.filter((c) => !previewHidden.includes(c.id)) }))
        .filter(
          (n) => n.channels.length > 0 || (n.category && !previewHidden.includes(n.category.id)),
        )
    : tree;
  const list = useRef<HTMLDivElement>(null);
  useChannelListKeys(list, toggle);
  // drag and drop: people onto voice channels, channels and categories into place
  const dnd = useSidebarDnd(list, gid);
  const dragKind = useSidebarDrag((s) => s.drag?.kind);
  const liftedCat = useSidebarDrag((s) =>
    s.drag?.kind === 'channel' && s.drag.category ? s.drag.id : null,
  );
  // navigating keeps the selected channel in view (§6.4), from the moment its
  // row exists (a cold start renders the list before READY fills it), and so
  // does a smaller window (the list gets shorter: 1024x640, 940x600)
  const selShown = !!selId && shown.some((n) => n.channels.some((c) => c.id === selId));
  useEffect(() => {
    const box = list.current;
    if (!selId || !selShown || !box) return;
    const show = () =>
      box
        .querySelector<HTMLElement>(`.ch[data-ch="${selId}"]`)
        ?.scrollIntoView?.({ block: 'nearest' });
    show();
    if (typeof ResizeObserver === 'undefined') return;
    let h = box.clientHeight;
    const ro = new ResizeObserver(() => {
      if (box.clientHeight < h) show();
      h = box.clientHeight;
    });
    ro.observe(box);
    return () => ro.disconnect();
  }, [selId, selShown]);
  return (
    <div className="hub-side nx-panel">
      <ServerHeader />
      <div
        className="hub-side-scroll nx-scroll"
        ref={list}
        role="group"
        aria-label={t('a11y.channels')}
        aria-description={t('ns.side.keys')}
        data-drag={dragKind}
      >
        {shown.map((node) => {
          const id = node.category?.id ?? 'none';
          const col = !!collapsed[id];
          const channels = node.channels.filter((c) => !col || c.id === selId || c.id === myVoice);
          return (
            <section
              key={id}
              className={'cat' + (col ? ' collapsed' : '') + (node.category ? '' : ' nocat')}
              data-cat-sec={id}
              data-dragging={liftedCat === id || undefined}
            >
              {node.category ? (
                <div className="ns-cat-row">
                  <button
                    type="button"
                    className="cat-h"
                    data-cat={id}
                    tabIndex={-1}
                    aria-expanded={!col}
                    aria-label={t('hub.cat.toggle', { name: node.category.name ?? '' })}
                    onClick={() => toggle(id)}
                    onContextMenu={(e) => {
                      // Mute category ›, Notification settings › (notify/menus.ts)
                      const items = channelNotifyItems(node.category!);
                      if (!items.length) return;
                      e.preventDefault();
                      openMenu(menuPoint(e), items);
                    }}
                  >
                    <Icon name="chev-down" cls="chev" />
                    <span dir="auto">{node.category.name}</span>
                  </button>
                  {manageableCats.includes(node.category.id) ? (
                    <button
                      type="button"
                      className="ns-cat-add"
                      aria-label={t('ns.admin.newChannelIn', { name: node.category.name ?? '' })}
                      data-tip={t('ns.admin.newChannel')}
                      onClick={() =>
                        openAdmin({
                          tab: 'channels',
                          id: 'new',
                          newType: 0,
                          parentId: node.category!.id,
                        })
                      }
                    >
                      <Icon name="plus" />
                    </button>
                  ) : null}
                </div>
              ) : null}
              <ul className="cat-list">
                {channels.map((c) => (
                  <ChannelRowWithPerms key={c.id} c={c} sel={c.id === selId} />
                ))}
              </ul>
            </section>
          );
        })}
        {!shown.length ? (
          <p className="ns-empty-side">{t(gid ? 'ns.hub.noChannels' : 'ns.hub.notMember')}</p>
        ) : null}
        {dnd.line}
      </div>
      {dnd.ghost}
    </div>
  );
}

/** The channel list's rows, in screen order (categories, channels, people in voice). */
const ROWS = '.cat-h, .ch, .vc-u';

/**
 * The channel list is one Tab stop (I10, I11): the selected channel's row
 * holds it, ↑/↓, Home and End move between rows, → and ← open and close a
 * category, Enter opens, Shift+F10 opens the row's menu. Anywhere in the
 * app, Alt+↑/↓ goes to the previous or next channel and Alt+Shift+↑/↓ to
 * the previous or next unread one, as in Discord.
 */
function useChannelListKeys(
  ref: React.RefObject<HTMLDivElement | null>,
  toggle: (id: string) => void,
) {
  const toggleRef = useRef(toggle);
  useEffect(() => {
    toggleRef.current = toggle;
  });
  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    const rows = () => [...box.querySelectorAll<HTMLElement>(ROWS)];
    // the one Tab stop: the focused row while focus is inside, else the selected channel
    const rest = () => {
      const all = rows();
      const sel = all.find((r) => r.getAttribute('aria-current') === 'page') ?? all[0];
      for (const r of all) r.tabIndex = r === sel ? 0 : -1;
    };
    const onFocusIn = (e: FocusEvent) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>(ROWS);
      if (!row || !box.contains(row)) return;
      for (const r of rows()) r.tabIndex = r === row ? 0 : -1;
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!box.contains(e.relatedTarget as Node | null)) rest();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const row = (e.target as HTMLElement).closest<HTMLElement>(ROWS);
      if (!row) return;
      const all = rows();
      const i = all.indexOf(row);
      let next: HTMLElement | undefined;
      if (e.key === 'ArrowDown') next = all[i + 1];
      else if (e.key === 'ArrowUp') next = all[i - 1];
      else if (e.key === 'Home') next = all[0];
      else if (e.key === 'End') next = all.at(-1);
      else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && row.matches('.cat-h')) {
        const open = row.getAttribute('aria-expanded') === 'true';
        if (open === (e.key === 'ArrowLeft')) toggleRef.current(row.dataset.cat ?? '');
        e.preventDefault();
        return;
      } else return;
      e.preventDefault();
      if (next) {
        next.focus();
        next.scrollIntoView?.({ block: 'nearest' });
      }
    };
    rest();
    // rows come and go (voice, categories): keep exactly one Tab stop
    const mo = new MutationObserver(() => {
      if (!box.contains(document.activeElement)) rest();
    });
    mo.observe(box, { childList: true, subtree: true });
    box.addEventListener('focusin', onFocusIn);
    box.addEventListener('focusout', onFocusOut);
    box.addEventListener('keydown', onKey);
    return () => {
      mo.disconnect();
      box.removeEventListener('focusin', onFocusIn);
      box.removeEventListener('focusout', onFocusOut);
      box.removeEventListener('keydown', onKey);
    };
  }, [ref]);
  // Alt+↑/↓: the previous or next channel; with Shift, the previous or next unread one
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const box = ref.current;
      if (!box || overlayOpen()) return;
      const chans = [...box.querySelectorAll<HTMLElement>('.ch')];
      const cur = chans.findIndex((c) => c.getAttribute('aria-current') === 'page');
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const fits = (c: HTMLElement) =>
        e.shiftKey
          ? c.dataset.st === 'unread' || c.dataset.st === 'mention'
          : c.dataset.type === 'text';
      for (let i = cur + step; i >= 0 && i < chans.length; i += step) {
        const c = chans[i]!;
        const id = c.dataset.ch;
        if (!id || !fits(c)) continue;
        e.preventDefault();
        // an unread voice channel opens its chat; it never joins the call
        goHub(
          c.dataset.type === 'voice'
            ? { kind: 'voice', channelId: id, chat: true }
            : { kind: 'channel', channelId: id },
        );
        return;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ref]);
}

function ChannelRowWithPerms({ c, sel }: { c: Channel; sel: boolean }) {
  // its settings: Manage Channels, or (a voice channel) Mute or Deafen
  // Members there, whose settings hold "Muted in this channel" (2026-10-28)
  const canManage = useData(
    (d) =>
      can(channelPermissions(d, c), Permissions.MANAGE_CHANNELS) ||
      ((c.type === ChannelType.GUILD_VOICE || c.type === ChannelType.GUILD_STAGE_VOICE) &&
        canSeeVoiceMutes(d, c)),
  );
  const noConnect = useData(
    (d) =>
      (c.type === ChannelType.GUILD_VOICE || c.type === ChannelType.GUILD_STAGE_VOICE) &&
      !can(channelPermissions(d, c), Permissions.CONNECT),
  );
  return <ChannelRow c={c} sel={sel} canManage={canManage} noConnect={noConnect} />;
}

export function HubCol() {
  const view = useUi((u) => u.hub);
  return (
    <div className="hub-col">
      <ViewAsBar />
      <Rail />
      <DeleteServerHost />
      {view?.kind === 'home' ? (
        <div className="hub-side nx-panel custom">
          <DmSidebar />
        </div>
      ) : (
        <ChannelSidebar />
      )}
    </div>
  );
}

/**
 * The open server's last text channel, else its first one you can read
 * (a cold start, a server opened from the rail). With no server opened yet
 * the last text channel anywhere opens, and its server with it.
 */
function useDefaultView(): HubView | null {
  const open = useUi((u) => u.guildId);
  const gid = useActiveGuildId();
  const tree = useChannelTree(gid);
  const lastText = useUi((u) => {
    const mine = gid ? u.lastByGuild[gid] : undefined;
    return mine ?? u.lastTextId;
  });
  const channels = useData((d) => d.channels);
  const last = lastText ? channels[lastText] : undefined;
  if (
    lastText &&
    last &&
    (!open || last.guild_id === gid) &&
    (last.type === ChannelType.GUILD_TEXT || threadOfView({ channels }, lastText))
  )
    return { kind: 'channel', channelId: lastText };
  for (const n of tree)
    for (const c of n.channels)
      if (c.type === ChannelType.GUILD_TEXT) return { kind: 'channel', channelId: c.id };
  return null;
}

const FRIENDS: HubView = { kind: 'home' };

export function HubMain() {
  const { t } = useT();
  const view = useUi((u) => u.hub);
  const fallback = useDefaultView();
  const channels = useData((d) => d.channels);
  // the voice room you are in, even one you cannot see (QA D2: a moderator
  // moved you into a hidden room; VoiceRoom shows it by the live roster)
  const inVoice = useVoice((v) => (v.session.status === 'idle' ? null : v.session.channelId));
  // a deleted or hidden channel falls back calmly; a DM that went stays in
  // Home, on Friends (Discord), instead of jumping to the server
  const valid =
    view &&
    (view.kind === 'home'
      ? !view.channelId || !!channels[view.channelId]
      : !!view.channelId &&
        (!!channels[view.channelId] || (view.kind === 'voice' && view.channelId === inVoice)));
  const dmGone = !valid && view?.kind === 'home';
  const v = valid ? view : dmGone ? FRIENDS : fallback;
  const inGuild = !!useActiveGuildId();
  // a thread opens beside its channel; a phone shows it full screen
  const thread = useData((d) => (v?.kind === 'channel' ? threadOfView(d, v.channelId) : null));
  const phone = useLayout().layout === 'phone';
  useEffect(() => {
    if (valid) return;
    if (dmGone) goHub(FRIENDS);
    else if (fallback) goHub(fallback);
  }, [valid, dmGone, fallback]);
  if (!v) {
    // removed from the server (kicked, banned): say so, not "no channels yet"
    return (
      <section className="hub-pane nx-panel ns-empty-pane">
        <div className="ch-start">
          <div className="ch-start-ic">
            <Icon name={inGuild ? 'hash' : 'lock'} />
          </div>
          <h2>{t(inGuild ? 'ns.hub.noChannelsTitle' : 'ns.hub.notMemberTitle')}</h2>
          <p>{t(inGuild ? 'ns.hub.noChannels' : 'ns.hub.notMember')}</p>
        </div>
      </section>
    );
  }
  if (v.kind === 'voice')
    return <VoiceRoom key={v.channelId} channelId={v.channelId} chatOpen={v.chat} />;
  if (v.kind === 'home') {
    if (v.page === 'requests' && !v.channelId) return <RequestsPage />;
    return v.channelId ? (
      <ChannelView key={v.channelId} channelId={v.channelId} />
    ) : (
      <FriendsPage />
    );
  }
  if (thread?.parent_id)
    return phone ? (
      <ThreadPage thread={thread} />
    ) : (
      <ChannelView key={thread.parent_id} channelId={thread.parent_id} threadId={thread.id} />
    );
  return <ChannelView key={v.channelId} channelId={v.channelId} />;
}

export { NxPermissions };
